/**
 * Mock Intake Patient Data
 *
 * Simulated unassigned patients for demo purposes
 */

import { IntakePatient } from '../types/intake.types';

/**
 * Generate mock intake patients
 *
 * Note: Provider type filter allows assignment to any role (RN, PT, OT)
 * Priority is based on expected discharge date - sooner discharge = higher priority
 *
 * Territory coverage:
 * - North: RN (3), PT (1), OT (1) - All types available
 * - South: RN (3), PT (1), OT (0) - No OT coverage
 * - East: RN (2), PT (0), OT (1) - No PT coverage
 * - West: RN (0), PT (0), OT (0) - No coverage at all
 *
 * Expected results (10 out of 12 have options):
 * - North patients (5): All have RN/PT/OT options
 * - South patients (3): Have RN/PT options, no OT
 * - East patients (2): Have RN/OT options, no PT
 * - West patients (2): No options for any type
 */
export function getMockIntakePatients(): IntakePatient[] {
  // Helper to add days to current date
  const addDays = (days: number): Date => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date;
  };

  // Distribution: today (30%), tomorrow (45%), +2 days (20%), +3 days (5%)
  const today = addDays(0);
  const tomorrow = addDays(1);
  const twoDaysOut = addDays(2);
  const threeDaysOut = addDays(3);
  const fourDaysOut = addDays(4);
  const fiveDaysOut = addDays(5);

  return [
    // Today - 30% (4 patients)
    {
      id: 1001,
      patientName: 'Smith, John',
      expectedDischargeDate: today, // Today - HIGH priority
      intakeVisitDate: today, // SOC scheduled same day as discharge
      requiredRole: 'RN',
      priority: 'HIGH',
      expectedVisitsPerWeek: 3,
      expectedDurationWeeks: 3,
      territory: 'North',
      status: 'unassigned',
    },
    {
      id: 1002,
      patientName: 'Doe, Jane',
      expectedDischargeDate: today, // Today - HIGH priority
      intakeVisitDate: tomorrow, // SOC scheduled within 24h
      requiredRole: 'RN',
      priority: 'HIGH',
      expectedVisitsPerWeek: 2,
      expectedDurationWeeks: 4,
      territory: 'South',
      status: 'unassigned',
    },
    {
      id: 1004,
      patientName: 'Garcia, Maria',
      expectedDischargeDate: today, // Today - HIGH priority
      intakeVisitDate: tomorrow, // SOC scheduled within 24h
      requiredRole: 'RN',
      priority: 'HIGH',
      expectedVisitsPerWeek: 3,
      expectedDurationWeeks: 4,
      territory: 'East',
      status: 'unassigned',
    },
    {
      id: 1012,
      patientName: 'Moore, Patricia',
      expectedDischargeDate: today, // Today - HIGH priority
      intakeVisitDate: twoDaysOut, // SOC scheduled within 48h
      requiredRole: 'RN',
      priority: 'MEDIUM',
      expectedVisitsPerWeek: 2,
      expectedDurationWeeks: 3,
      territory: 'South',
      status: 'unassigned',
    },

    // Tomorrow
    {
      id: 1003,
      patientName: 'Johnson, Robert',
      expectedDischargeDate: tomorrow, // Tomorrow - MEDIUM priority
      intakeVisitDate: twoDaysOut, // SOC scheduled within 24h of discharge
      requiredRole: 'RN',
      priority: 'MEDIUM',
      expectedVisitsPerWeek: 2,
      expectedDurationWeeks: 2,
      territory: 'North',
      status: 'unassigned',
    },
    {
      id: 1006,
      patientName: 'Williams, Sarah',
      expectedDischargeDate: tomorrow, // Tomorrow - MEDIUM priority
      intakeVisitDate: twoDaysOut, // SOC scheduled within 24h
      requiredRole: 'RN',
      priority: 'MEDIUM',
      expectedVisitsPerWeek: 2,
      expectedDurationWeeks: 3,
      territory: 'North',
      status: 'unassigned',
    },
    {
      id: 1007,
      patientName: 'Brown, David',
      expectedDischargeDate: tomorrow, // Tomorrow - MEDIUM priority
      intakeVisitDate: twoDaysOut, // SOC scheduled within 48h
      requiredRole: 'RN',
      priority: 'MEDIUM',
      expectedVisitsPerWeek: 3,
      expectedDurationWeeks: 2,
      territory: 'East',
      status: 'unassigned',
    },
    {
      id: 1010,
      patientName: 'Martinez, Lisa',
      expectedDischargeDate: tomorrow, // Tomorrow - HIGH priority
      intakeVisitDate: threeDaysOut, 
      requiredRole: 'RN',
      priority: 'MEDIUM',
      expectedVisitsPerWeek: 3,
      expectedDurationWeeks: 3,
      territory: 'West',
      status: 'unassigned',
    },
    {
      id: 1011,
      patientName: 'Wilson, Thomas',
      expectedDischargeDate: tomorrow, // Tomorrow - MEDIUM priority
      intakeVisitDate: twoDaysOut, // SOC scheduled within 24h
      requiredRole: 'RN',
      priority: 'MEDIUM',
      expectedVisitsPerWeek: 3,
      expectedDurationWeeks: 2,
      territory: 'North',
      status: 'unassigned',
    },

    // Two days out
    {
      id: 1005,
      patientName: 'Chen, Michael',
      expectedDischargeDate: twoDaysOut, // Two days out - LOW priority
      intakeVisitDate: fourDaysOut, 
      requiredRole: 'RN',
      priority: 'LOW',
      expectedVisitsPerWeek: 2,
      expectedDurationWeeks: 3,
      territory: 'West',
      status: 'unassigned',
    },
    {
      id: 1008,
      patientName: 'Taylor, Emily',
      expectedDischargeDate: twoDaysOut, // Two days out - LOW priority
      intakeVisitDate: fourDaysOut, // SOC scheduled within 24h
      requiredRole: 'RN',
      priority: 'LOW',
      expectedVisitsPerWeek: 2,
      expectedDurationWeeks: 4,
      territory: 'South',
      status: 'unassigned',
    },

    // Three days out
    {
      id: 1009,
      patientName: 'Anderson, James',
      expectedDischargeDate: threeDaysOut, // Three days out - LOW priority
      intakeVisitDate: fiveDaysOut, // SOC scheduled same day as discharge
      requiredRole: 'RN',
      priority: 'LOW',
      expectedVisitsPerWeek: 2,
      expectedDurationWeeks: 3,
      territory: 'North',
      status: 'unassigned',
    },
  ];
}

/**
 * Add territory to existing provider capacity data
 *
 * Distributed across territories to ensure patients have options
 */
export function addTerritoryToProviders() {
  // Territory assignment based on provider ID
  const territoryMap: { [providerId: number]: string } = {
    // RN providers (1-8)
    1: 'North',  // RN - High util
    2: 'South',  // RN - Very high util
    3: 'East',   // RN - Medium-high util
    4: 'North',  // RN - Medium util
    5: 'South',  // RN - Low util (best)
    6: 'East',   // RN - Low util
    7: 'North',  // RN - Medium util
    8: 'South',  // RN - Medium-high util
    // PT providers (9-10)
    9: 'North',  // PT - Medium util
    10: 'South', // PT - High util
    // OT providers (11-12)
    11: 'East',  // OT - Low util (best for OT)
    12: 'North', // OT - Medium util
  };

  return territoryMap;
}

/**
 * Mock panel sizes for providers
 *
 * Varied panel sizes to create different recommendation scores:
 * - High utilization providers have large panels
 * - Low utilization providers have small panels
 */
export function getMockPanelSizes(): { [providerId: number]: number } {
  return {
    // RN providers (1-8)
    1: 20, // RN - High utilization, large panel -> Low scores
    2: 22, // RN - Very high utilization, very large panel -> Lowest scores
    3: 15, // RN - Medium-high utilization, medium panel -> Medium scores
    4: 12, // RN - Medium utilization, small-medium panel -> Good scores
    5: 8,  // RN - Low utilization, small panel -> Best scores
    6: 10, // RN - Low utilization, small panel -> Good scores
    7: 14, // RN - Medium utilization, medium panel
    8: 16, // RN - Medium-high utilization, medium-large panel
    // PT providers (9-10)
    9: 13,  // PT - Medium utilization, medium panel -> Good scores
    10: 18, // PT - High utilization, large panel -> Lower scores
    // OT providers (11-12)
    11: 9,  // OT - Low utilization, small panel -> Best scores for OT
    12: 14, // OT - Medium utilization, medium panel -> Good scores
  };
}
